import { noise1, pulse, seeded, seg, tween } from '../../../core/effect/motion';
import { add, canvasOf, fade, lifeCenter, place } from './common';
import { arrowEnds } from './attackArrow';
import { DAMAGE_SLASH, lifeNumberTexture, makeLifeNumber } from './lifeNumber';

/**
 * **ダメージ**（Issue #554。指示書 `../direction/damage.md`）。
 *
 * **誰が何ダメージ受けたか**を、数字を叩きつけて LP へ運ぶことで見せる。
 *
 * ```
 * 0     ばんっ！: 発生場所に「-700」（銀の金属文字＋黒の縁、赤の斬り傷を差す）が 2.5 倍から 1 倍へ叩きつけられる。
 *       硬い火花・白い一瞬の閃光（grade の 2 フレーム）
 * 60    止め（ヒットストップ 80ms）
 * 140   見得: 数字が小さく震える。揺れ（scene.shake）が抜ける
 * 450   LP へ: ダメージを受けた人の LP の数字へ、加速しながら縮んで飛ぶ（残像 2 枚）
 * 900   着く瞬間に消え、LP の枠が揺れて一瞬光る（`hud`）。LP の上に硬い輪と火花
 * 1100  終わり
 * ```
 *
 * 発生場所: 戦闘ダメージは**被攻撃モンスター**（battle が画面を切ったのと同じ点＝対象のカードの中心）、
 * 直接攻撃は**突っ込んだ先**（`fx/attackArrow.js` の直接攻撃の先）、**効果ダメージは画面の中央**。
 * 戦闘かどうかは状況（`battle` / `effect`。分岐レイヤー `editorPorts/duelSituations/damage.js` が直前の battle から決め、
 * 攻撃するカード・対象を payload の `battle` に入れる。Issue #589）。
 * もやもやした光は使わない（閃光は grade、火花・輪・斬り傷は硬い形）。
 */
const D = 1100;
const SLAM = 60;
const STOP = 80;
const HOLD = SLAM + STOP;
const FLY = 450;
const ARRIVE = 900;

/** 数字の大きさ（px。1 倍のとき）と、叩きつけの始まりの倍率 */
const SIZE = 170;
const SLAM_FROM = 2.5;
/** LP に着くときの倍率 */
const LAND_S = 0.32;

/** LP が数字の着いてから本来の値へ減りきる時間（帯の終わりより前に減りきる＝終わりで HUD の本来の値へ継ぎ目なく戻る） */
const DROP = 180;

/**
 * **その間 HUD に見せる LP**（Issue #554。人の判断: LP は数字が着いてから減る）。
 * 着くまでは減る前の LP（`ctx.hud.lifeFrom`）、着いたら本来の値（`lifeTarget`）へ `DROP` で減る。
 * HUD の状態は書き換えない（`hud` の作用 `life` で見せ方だけ差し替える）。値が無ければ何も頼まない。
 */
function shownLife(hud, player, t) {
  const p = player === 1 ? 1 : 0;
  const from = hud?.lifeFrom?.[p];
  const to = hud?.lifeTarget?.[p];
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(from > to)) return null;
  if (t < ARRIVE) return from;
  return from + (to - from) * tween(t, ARRIVE, ARRIVE + DROP, 'ease_out');
}

/**
 * **数字の生まれる場所**（舞台のピクセル）。写らなければ画面の中央。
 * `kind`: `battle`（対象のカード）/ `direct`（直接攻撃の先）/ `effect`（画面の中央）
 */
function originOf(ctx, battle) {
  const { w: W, h: H } = canvasOf(ctx);
  const center = [W / 2, H / 2];
  const b = battle ? ctx.payload?.battle : null;
  if (!b) return center;
  if (b.target?.uid || b.target?.zone) {
    const a = ctx.anchor?.({ card: b.target.uid ?? '', else: { zone: b.target.zone ?? '' } });
    return a?.screen ?? center;
  }
  const ends = arrowEnds(ctx.stage?.field?.zoneQuad, { fromZone: b.attacker?.zone, direct: true, player: b.player ?? 0 });
  return ends?.tip ?? center;
}

/** ダメージの組み立て。`battle` = 戦闘ダメージ（数字は対象のカード／直接攻撃の先で生まれる）/ false = 効果ダメージ（画面の中央） */
export const damageSpec = (battle) => ({
  label: 'ダメージ',
  color: '#ff2e3c',
  duration: () => D,
  scene: [
    {
      ms: SLAM,
      grade: {
        contrast: { keys: [{ ms: 0, value: 1.35 }, { ms: 34, value: 1.35, ease: 'hold' }, { ms: 35, value: 1.1 }, { ms: STOP + 160, value: 1, ease: 'ease_out' }] },
        brightness: { keys: [{ ms: 0, value: 1.15 }, { ms: 34, value: 1.15, ease: 'hold' }, { ms: 35, value: 1 }] },
        chroma: { keys: [{ ms: 0, value: 0.01 }, { ms: STOP + 200, value: 0, ease: 'ease_out' }] },
      },
    },
    { ms: HOLD, shake: { amp: 16, freq: 26, decayMs: 300 } },
  ],
  sounds: () => [{ at: SLAM - 20, name: 'attack', gain: 0.7 }, { at: ARRIVE - 40, name: 'life_down' }],
  hud: ({ payload, localMs, hud }) => [{
    anchor: 'life',
    player: payload.player,
    ...(shownLife(hud, payload.player, localMs) != null ? { life: shownLife(hud, payload.player, localMs) } : {}),
    shake: pulse(localMs, { at: ARRIVE, inMs: 10, hold: 70, outMs: 110 }),
    flash: pulse(localMs, { at: ARRIVE, inMs: 10, hold: 40, outMs: 90 }),
    color: '#ff2e3c',
    phase: localMs,
  }],

  layers: [{
    id: 'number',
    label: 'ダメージの数字',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { w: W, h: H } = canvasOf(ctx);
      const { payload } = ctx;
      const root = new THREE.Group();
      const rand = seeded(`${ctx.seed}:damage`);

      const tex = lifeNumberTexture(kit, payload.amount, SIZE);
      const nw = tex.width; const nh = tex.height;
      /*
       * 数字が**叩きつけの最大（2.5 倍）の時点でも**画面に収まるように寄せる（直接攻撃の先は画面の端に近い。人の判断 2026-09-27）。
       * 収めるのは文字の形そのもの（テクスチャの余白と後ろの斬り傷は数えない）
       */
      const o = originOf(ctx, battle);
      const pad = Math.max(60, SIZE * 0.14 + SIZE * 0.11 * 2 + 10);
      const MARGIN = 24;
      const mx = Math.min(W / 2, (Math.max(0, nw - pad * 2) / 2) * SLAM_FROM + MARGIN);
      const my = Math.min(H / 2, (SIZE * 0.62) * SLAM_FROM + MARGIN);
      const sx = Math.max(mx, Math.min(W - mx, o[0]));
      const sy = Math.max(my, Math.min(H - my, o[1]));
      const [ex, ey] = lifeCenter(ctx, payload.player);

      const plainMesh = (geo, color) => {
        const m = new THREE.Mesh(geo, kit.plain(null, 1));
        m.material.color?.set?.(color);
        return m;
      };
      const bar = (color) => plainMesh(new THREE.PlaneGeometry(1, 1), color);
      const tri = (color) => plainMesh(new THREE.CircleGeometry(1, 3), color);

      const main = makeLifeNumber(ctx, root, tex, { order: 10, colors: DAMAGE_SLASH, name: 'damage-number' });
      // 飛ぶ間の硬い線（数字の後ろに引く。白の芯と赤の脇）
      const streaks = [-1, 0, 1].map((k) => ({ m: add(root, bar(k === 0 ? '#ffffff' : '#ff2e3c'), 7), k }));

      // 叩きつけの火花（硬い三角。四方へ散る）
      const sparkCols = ['#ffffff', '#ffd21a', '#ff2e3c', '#fff3b0'];
      const sparks = Array.from({ length: 22 }, (_, i) => ({
        m: add(root, tri(sparkCols[i % sparkCols.length]), 12),
        a: rand() * Math.PI * 2,
        len: i < 8 ? 110 + rand() * 80 : 40 + rand() * 50,
        w: i < 8 ? 12 + rand() * 6 : 5 + rand() * 5,
        speed: 900 + rand() * 1100,
        r0: nh * 0.25 + rand() * nw * 0.2,
        life: 180 + rand() * 170,
      }));
      // 着いたところの硬い輪（細い板を輪に並べる）と火花
      const RING = 18;
      const ring = Array.from({ length: RING }, () => add(root, bar('#ffffff'), 13));
      const landSparks = Array.from({ length: 10 }, (_, i) => ({
        m: add(root, tri(i % 2 ? '#ff2e3c' : '#ffffff'), 13),
        a: rand() * Math.PI * 2,
        len: 40 + rand() * 40,
        speed: 600 + rand() * 500,
        life: 120 + rand() * 60,
      }));

      /** 時刻 → 数字の位置・倍率・回し・濃さ */
      const pose = (t) => {
        const slam = tween(t, 0, SLAM, 'power3_in');
        const fly = tween(t, FLY, ARRIVE, 'power3_in');
        let s = SLAM_FROM + (1 - SLAM_FROM) * slam;
        // 当たった瞬間に少し潰れて戻る
        const squash = t >= SLAM ? 0.1 * pulse(t, { at: SLAM, inMs: 0, hold: STOP, outMs: 140 }) : 0;
        s *= 1 - (1 - LAND_S) * fly;
        // 見得の震え（止めの間は止まる）
        const tremble = t >= HOLD && t < FLY ? 4 * (1 - seg(t, HOLD, FLY) * 0.5) : 0;
        const jx = tremble * noise1('dmg-x', t * 0.05);
        const jy = tremble * noise1('dmg-y', t * 0.05);
        const x = sx + (ex - sx) * fly + jx;
        const y = sy + (ey - sy) * fly - Math.sin(fly * Math.PI) * 80 + jy;
        const a = (t < ARRIVE ? 1 : 0) * (1 - seg(t, ARRIVE - 40, ARRIVE));
        return { x, y, sx: s * (1 + squash), sy: s * (1 - squash), rot: -0.06 * (1 - fly), a };
      };
      const put = (n, p, alpha) => {
        place(n.g, p.x, p.y, { sx: p.sx, sy: p.sy, rot: p.rot });
        fade(n.g, alpha);
      };

      return {
        root,
        update: ({ localMs: t }) => {
          put(main, pose(t), pose(t).a);
          // 飛ぶ間の線: 今の位置から少し前の位置まで（速いほど長い）
          const now = pose(t); const prev = pose(Math.max(FLY, t - 90));
          const vx = now.x - prev.x; const vy = now.y - prev.y;
          const vl = Math.hypot(vx, vy);
          const flying = t > FLY + 30 && t < ARRIVE - 20 && vl > 4;
          const dir = Math.atan2(vy, vx);
          streaks.forEach(({ m, k }) => {
            const off = k * nh * 0.18 * now.sy;
            const len = vl * (k === 0 ? 1.4 : 1);
            const cx = now.x - Math.cos(dir) * len / 2 - Math.sin(dir) * off;
            const cy = now.y - Math.sin(dir) * len / 2 + Math.cos(dir) * off;
            place(m, cx, cy, { sx: Math.max(0.001, len), sy: k === 0 ? 5 : 3, rot: dir });
            fade(m, flying ? 0.9 : 0);
          });
          // 叩きつけの火花: 当たった瞬間に出て、止めの間は止まり、そのあと飛ぶ
          const since = t < SLAM ? -1 : (t < HOLD ? 0 : t - HOLD);
          sparks.forEach((s) => {
            const k = since / s.life;
            if (since < 0 || k >= 1) { fade(s.m, 0); return; }
            const r = s.r0 + s.speed * (since / 1000) * (1 - 0.4 * k);
            const l = s.len * (1 - k) * (t < HOLD ? 0.6 : 1);
            place(s.m, sx + Math.cos(s.a) * (r + l / 2), sy + Math.sin(s.a) * (r + l / 2), {
              sx: Math.max(0.001, l), sy: s.w * (1 - 0.5 * k), rot: s.a,
            });
            fade(s.m, 1);
          });
          // 着いたところ: 硬い輪が広がりながら細くなる・火花
          const lk = seg(t, ARRIVE, ARRIVE + 170);
          const onLand = t >= ARRIVE && lk < 1;
          const R = 30 + 110 * tween(t, ARRIVE, ARRIVE + 170, 'expo_out');
          ring.forEach((m, i) => {
            const a = (i / RING) * Math.PI * 2;
            place(m, ex + Math.cos(a) * R, ey + Math.sin(a) * R, {
              sx: (2 * Math.PI * R) / RING * 1.05, sy: Math.max(0.001, 8 * (1 - lk)), rot: a + Math.PI / 2,
            });
            fade(m, onLand ? 1 : 0);
          });
          landSparks.forEach((s) => {
            const since2 = t - ARRIVE;
            const k = since2 / s.life;
            if (since2 < 0 || k >= 1) { fade(s.m, 0); return; }
            const r = 20 + s.speed * (since2 / 1000);
            const l = s.len * (1 - k);
            place(s.m, ex + Math.cos(s.a) * r, ey + Math.sin(s.a) * r, { sx: Math.max(0.001, l), sy: 6 * (1 - k), rot: s.a });
            fade(s.m, 1);
          });
        },
      };
    },
  }],
});
